import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import useLog from "../hooks/useLog.hk";
import useLogin from "../hooks/useLogin.hk";
import { 
  videoTogglePause, 
  toSerializableVideoElement, 
  videoPlay, 
  videoProgress, 
  videoTime,
  videoEnd,
  clearStartTime
} from "../store/features/VideoSlice";
import { googleAnalyticsTracker } from "../utils/google";
import { getHost } from "../utils/host";
import CmpPlayerControls from "./playerControls.cmp";
import "./videoPlayer.cmp.scss";

const CmpVideoPlayer = () => {
    const [ , logProgress, logEnd ] = useLog();
    const [ firstPlay, setFirstPlay ] = useState(true);
    const [ showThumb, setShowThumb ] = useState(true);
    const [ disableScroll, setDisableScroll ] = useState(true);
    
    const video = useSelector(state => state.video.currVideo);
    const startTime = useSelector(state => state.video.videoProgress.startTime);
    const autoplay = useSelector(state => state.video.videoProgress.autoplay);
    const vProgInfo = useSelector(state => state.video.videoProgress.info);

    const videoEl = useRef();
    const videoContainer = useRef();
    const dispatch = useDispatch();
    const [ user ] = useLogin();

    const handleTimeUpdate = (vElement) => {
        dispatch(videoProgress(toSerializableVideoElement(vElement)));
        dispatch(videoTime(Math.floor(vElement.currentTime)));
    };

    const handlePlay = () => {
        if (showThumb) { 
            googleAnalyticsTracker("video", video.title, user.username); 
        }
        setShowThumb(false);
        dispatch(videoTogglePause(false));
        
        if (!firstPlay) return;
        dispatch(videoPlay({ video }));
        setFirstPlay(false);
    };

    // 1. Reset thumbnail & handle scrolling when video changes
    useEffect(() => {
        setShowThumb(!autoplay);
        setFirstPlay(true);

        if (disableScroll && video) {
            setDisableScroll(false);
            return;
        }

        if (videoContainer.current) {
            videoContainer.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [video?.videoId]);

    // 2. Handle startTime changes (e.g. clicking timestamp link while video is already mounted)
    useEffect(() => {
        if (startTime > 0) {
            // If thumbnail is showing and a timestamp is requested, hide thumbnail to mount video
            if (showThumb && startTime > 0) {
                setShowThumb(false);
            }

            // Seek directly if video element is already active
            if (videoEl.current) {
                videoEl.current.currentTime = startTime;
                videoEl.current.play().catch(() => {});
				dispatch(clearStartTime());
            }
        }
    }, [startTime, showThumb, dispatch]);

    // 3. Ensure seeking happens when video metadata loads after thumbnail click
    const handleLoadedMetadata = () => {
        if (videoEl.current && startTime > 0) {
            videoEl.current.currentTime = startTime;
			dispatch(clearStartTime());
        }
    };

    useEffect(() => {
        const handleUnload = () => {
            const vid = videoEl.current;
            if (vid && vid.played.length > 0 && !vid.paused) {
                googleAnalyticsTracker("videoplay", JSON.stringify(vProgInfo), user.username);
                logProgress(true);
            }
        };

        window.addEventListener("beforeunload", handleUnload);
        return () => window.removeEventListener("beforeunload", handleUnload);
    }, [logProgress, user?.username, vProgInfo]);

    const handlePause = () => {
        dispatch(videoTogglePause(true));
        logProgress();
        googleAnalyticsTracker("videoplay", JSON.stringify(vProgInfo), user.username);
    };
    
    const handleEnd = () => {
        dispatch(videoTogglePause(true));
        dispatch(videoEnd()); // Fixed: Invoked action function
        logProgress().then(logEnd);
    };

    if (!video) return null;

    return (
        <div className="cmp-video-player" ref={videoContainer} tabIndex={showThumb ? -1 : 0}>
            {showThumb ? (
                <>
                    <img className="img-thumb" src={`${getHost()}${video.thumb}`} alt="" />
                    <div className="overlay" onClick={handlePlay}>
                        <FontAwesomeIcon icon="play-circle" />
                    </div>
                </>
            ) : (
                <>
                    <video
                        ref={videoEl}
                        src={`${getHost()}${video.url}`}
                        onPlay={handlePlay}
                        onTimeUpdate={(e) => handleTimeUpdate(e.target)}
                        onEnded={handleEnd}
                        onPause={handlePause}
                        onContextMenu={(e) => e.preventDefault()}
                        onLoadedMetadata={handleLoadedMetadata}
                        playsInline
                        autoPlay
                    />
                    <CmpPlayerControls videoRef={videoEl} containerRef={videoContainer} video={video} />
                </>
            )}
        </div>
    );
};

export default CmpVideoPlayer;